// NOSONAR
/* eslint-disable max-lines, max-lines-per-function, complexity */
/* eslint-disable */
import axios from 'axios';
import { fetch_form_structure } from '@/services/main_section_service';
import {
  fetch_assign_form_status_data,
  fetch_entity_type_workflow_forms,
  fetch_workflow_statuses
} from '@/services/workflow_service';

import {
  CREATE_ENTITY_APPLICATION_ONLY_URL,
  CREATE_NEW_ENTITY_URL,
  APPLY_FOR_ENTITY_TYPE_URL,
  EVALUATE_ONBOARDING_COMPLIANCE_URL,
  EXECUTE_ONBOARDING_URL,
  GET_MY_ASSIGNED_APPLICATIONS_API_URL,
  GET_ONBOARDING_DASHBOARD_URL,
  GET_USERS,
  GET_ONBOARDING_STATUS_URL,
  GET_ENTITY_TYPE_ASSIGNED_QUESTIONS_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  GET_ENTITY_TYPE_FORMS_URL,
  GET_FORMS_URL,
  LIST_ENTITY_CONFIGS_URL,
  LIST_APPLICATIONS_API_URL,
  PREPARE_ONBOARDING_URL,
  VIEW_ENTITY_TYPE_URL,
  VIEW_PROJECTS,
  get_request_config
} from '@/utils/system_constants/constant.js';
import {
  extract_items,
  first_nested_value,
  first_value,
  normalize_collection,
  normalize_text,
  parse_response_data,
  title_case
} from '@/services/onboarding_data_helpers';
import {
  build_local_onboarding_case_metadata,
  calculate_stats,
  get_form_id,
  get_form_name,
  infer_journey_key,
  map_case,
  record_matches_journey,
  sort_cases_newest_first,
  write_local_onboarding_case_metadata
} from '@/services/onboarding_case_service_helpers';
import {
  normalize_gate_stage_id,
  resolve_workflow_display_stage
} from '@/services/onboarding_case_detail_helpers';
import {
  dedupe_cases,
  extract_config_form_bindings,
  extract_config_workflow_bindings,
  extract_entity_type_forms,
  normalize_onboarding_entity_type,
  get_workflow_binding_id,
  JOURNEY_LABELS,
  normalize_form_binding,
  onboarding_request_cache,
  ONBOARDING_EXCLUDED_PROJECT_NAMES,
  map_with_concurrency_limit,
  read_local_onboarding_case_metadata
} from '@/services/onboarding_journey_service_helpers';
import {
  fetch_application_documents,
  fetch_application_form,
  fetch_application_forms,
  fetch_application_profile_completion_summary,
  fetch_entity_profile,
  fetch_sent_contracts
} from '@/services/onboarding_journey_application_service';

export {
  build_manual_verification_checks,
  build_verification_checks
} from '@/services/onboarding_verification_helpers';
export {
  build_case_gate_journey,
  build_previous_reviewable_gate,
  build_agreement_rows,
  build_case_form_sections,
  build_contact_rows,
  build_profile_rows,
  build_questionnaire_answers,
  resolve_gate_id_from_stage_label,
  resolve_workflow_display_stage,
  build_transition_stage_timeline,
  build_stage_timeline,
  build_summary_rows
} from '@/services/onboarding_case_detail_helpers';
export {
  build_dashboard_stats,
  build_funnel_steps,
  build_queue_items
} from '@/services/onboarding_overview_helpers';
export { build_journey_filters } from '@/services/onboarding_journey_service_helpers';
export {
  fetch_aml_result,
  fetch_credit_report_result,
  fetch_entity_type_verification_bindings,
  fetch_idv_result,
  fetch_latest_kyc_result,
  fetch_onboarding_services,
  resolve_runnable_application_forms_for_service,
  run_auto_verification
} from '@/services/onboarding_verification_service';
export {
  fetch_bank_statement_analysis,
  fetch_application_documents,
  fetch_application_form,
  fetch_application_forms,
  fetch_application_profile_completion,
  fetch_application_profile_completion_summary,
  fetch_application_transition_history,
  fetch_entity_profile,
  fetch_manual_verification_history,
  fetch_manual_verification_question_mappings,
  fetch_manual_verification_run,
  fetch_manual_verification_runs,
  fetch_manual_verification_status,
  fetch_onboarding_verification_question_mappings,
  fetch_verification_logs,
  fetch_verification_statuses,
  open_manual_verification_run,
  refresh_bank_statement_analysis,
  record_manual_verification_decision,
  record_manual_verification_field_result,
  start_bank_statement_analysis,
  submit_application_form_answers,
  submit_application_form_file_answers
} from '@/services/onboarding_journey_application_service';

export const fetch_onboarding_cases = async (journey_key, params = {}) => {
  const response = await axios.get(LIST_APPLICATIONS_API_URL, get_request_config({ params }));
  const requested_journey = String(journey_key || '').trim();
  const cases = await hydrate_cases_with_runtime_progress(extract_items(response)
    .map((record) => map_case(record, requested_journey || infer_journey_key(record)))
    .filter((record) => (
      !requested_journey
      || String(record?.journey_tone || record?.entity_type_id || '') === requested_journey
      || record_matches_journey(record, requested_journey)
    )));

  return sort_cases_newest_first(cases);
};

export const fetch_onboarding_dashboard = async (params = {}) => {
  const normalized_params = { ...(params || {}) };
  const cache_key = JSON.stringify(normalized_params);
  if (onboarding_request_cache.onboarding_status.has(`dashboard:${cache_key}`)) {
    return onboarding_request_cache.onboarding_status.get(`dashboard:${cache_key}`);
  }

  const request = axios.get(
    GET_ONBOARDING_DASHBOARD_URL,
    get_request_config({ params: normalized_params })
  )
    .then((response) => {
      const parsed = parse_response_data(response);
      return parsed?.data && typeof parsed.data === 'object'
        ? parsed.data
        : (parsed || {});
    })
    .catch((error) => {
      onboarding_request_cache.onboarding_status.delete(`dashboard:${cache_key}`);
      throw error;
    });

  onboarding_request_cache.onboarding_status.set(`dashboard:${cache_key}`, request);
  return request;
};

export const fetch_all_onboarding_cases = async (params = {}) => {
  const normalized_params = { ...(params || {}) };
  const include_runtime_progress = normalized_params.include_runtime_progress !== false;
  delete normalized_params.include_runtime_progress;
  const cache_key = JSON.stringify({
    params: normalized_params,
    include_runtime_progress
  });
  if (onboarding_request_cache.all_cases.has(cache_key)) {
    return onboarding_request_cache.all_cases.get(cache_key);
  }

  const request = Promise.all([
    axios.get(LIST_APPLICATIONS_API_URL, get_request_config({ params: normalized_params })),
    fetch_onboarding_entity_types().catch(() => [])
  ])
    .then(async ([response, entity_types]) => {
      const mapped_cases = extract_items(response).map((record) => map_case(record, infer_journey_key(record)));
      const base_cases = dedupe_cases(mapped_cases);
      const hydrated_cases = include_runtime_progress
        ? await hydrate_cases_with_runtime_progress(base_cases)
        : base_cases;
      return sort_cases_newest_first(filter_cases_by_active_entity_types(hydrated_cases, entity_types));
    })
    .catch((error) => {
      onboarding_request_cache.all_cases.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.all_cases.set(cache_key, request);
  return request;
};

function is_active_entity_type(entity_type = {}) {
  return entity_type?.is_active !== false;
}

function build_active_entity_type_id_set(entity_types = []) {
  return new Set(
    (Array.isArray(entity_types) ? entity_types : [])
      .map((entity_type) => Number(entity_type?.id || entity_type?.entity_type_id || 0))
      .filter((entity_type_id) => entity_type_id > 0)
  );
}

function get_case_entity_type_id(case_item = {}) {
  return Number(
    case_item?.entity_type_id
    || case_item?.threshold_entity_type_id_used
    || case_item?.entity_type?.id
    || 0
  );
}

function filter_cases_by_active_entity_types(cases = [], entity_types = []) {
  const active_entity_type_ids = build_active_entity_type_id_set(entity_types);
  if (!active_entity_type_ids.size) return [];

  return (Array.isArray(cases) ? cases : []).filter((case_item) => {
    const entity_type_id = get_case_entity_type_id(case_item);
    return entity_type_id > 0 && active_entity_type_ids.has(entity_type_id);
  });
}

export const fetch_onboarding_entity_types = async () => {
  const cache_key = 'all';
  if (onboarding_request_cache.entity_types.has(cache_key)) {
    return onboarding_request_cache.entity_types.get(cache_key);
  }

  const request = axios.get(VIEW_ENTITY_TYPE_URL, get_request_config())
    .then((response) => normalize_collection(response)
      .map((entity_type) => normalize_onboarding_entity_type(entity_type))
      .filter((entity_type) => entity_type.id && entity_type.name && is_active_entity_type(entity_type)))
    .catch((error) => {
      onboarding_request_cache.entity_types.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.entity_types.set(cache_key, request);
  return request;
};

export const fetch_configured_onboarding_entity_types = async () => {
  const cache_key = 'configured';
  if (onboarding_request_cache.entity_types.has(cache_key)) {
    return onboarding_request_cache.entity_types.get(cache_key);
  }

  const request = axios.get(LIST_ENTITY_CONFIGS_URL, get_request_config())
    .then((response) => normalize_collection(response)
      .filter((config) => config?.is_active !== false)
      .map((config) => normalize_onboarding_entity_type({
        ...config,
        entity_type_id: config?.entity_type,
        entity_type_name: config?.entity_type_name
      }))
      .filter((entity_type) => entity_type.id && entity_type.name && is_active_entity_type(entity_type)))
    .catch((error) => {
      onboarding_request_cache.entity_types.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.entity_types.set(cache_key, request);
  return request;
};

export const fetch_project_onboarding_entity_types = async (project_id) => {
  if (!project_id) return [];
  const cache_key = String(project_id);
  if (onboarding_request_cache.entity_types.has(cache_key)) {
    return onboarding_request_cache.entity_types.get(cache_key);
  }

  const request = axios.get(
    VIEW_ENTITY_TYPE_URL,
    get_request_config({ params: { project_id } })
  )
    .then((response) => normalize_collection(response)
      .map((entity_type) => normalize_onboarding_entity_type(entity_type))
      .filter((entity_type) => entity_type.id && entity_type.name && is_active_entity_type(entity_type)))
    .catch((error) => {
      onboarding_request_cache.entity_types.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.entity_types.set(cache_key, request);
  return request;
};

export const fetch_onboarding_forms = async () => {
  const response = await axios.get(GET_FORMS_URL, get_request_config());
  return normalize_collection(response)
    .map((form) => ({ ...form, id: get_form_id(form), name: get_form_name(form) }))
    .filter((form) => form.id);
};

export const fetch_onboarding_forms_for_entity_type = async (entity_type_id, options = {}) => {
  if (!entity_type_id) return [];
  const cache_key = [
    entity_type_id,
    options?.project_id || '',
    options?.sub_entity_type_id || ''
  ].map((value) => String(value)).join(':');
  if (onboarding_request_cache.forms_for_entity_type.has(cache_key)) {
    return onboarding_request_cache.forms_for_entity_type.get(cache_key);
  }

  const request = fetch_uncached_onboarding_forms_for_entity_type(entity_type_id, options)
    .catch((error) => {
      onboarding_request_cache.forms_for_entity_type.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.forms_for_entity_type.set(cache_key, request);
  return request;
};

async function fetch_uncached_onboarding_forms_for_entity_type(entity_type_id, options = {}) {
  const params = { entity_type_id };
  if (options?.project_id) params.project_id = options.project_id;
  if (options?.sub_entity_type_id) params.sub_entity_type_id = options.sub_entity_type_id;

  const config_response = await axios.get(
    GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
    get_request_config({ params })
  ).catch(() => null);

  const config_forms = extract_config_form_bindings(config_response)
    .map((binding) => normalize_form_binding(binding))
    .filter((form) => form.id && form.is_active !== false)
    .sort((left, right) => Number(left?.priority || 0) - Number(right?.priority || 0));

  if (config_response) {
    return config_forms;
  }

  const response = await axios.get(
    GET_ENTITY_TYPE_FORMS_URL,
    get_request_config({ params: { entity_type_id } })
  );

  const direct_forms = extract_entity_type_forms(response)
    .map((form) => ({ ...form, id: get_form_id(form), name: get_form_name(form) }))
    .filter((form) => form.id);

  if (direct_forms.length) {
    return direct_forms;
  }

  return [];
}

export const fetch_onboarding_entity_type_questions = async (entity_type_id) => {
  if (!entity_type_id) return [];
  const cache_key = String(entity_type_id);
  if (onboarding_request_cache.entity_type_questions.has(cache_key)) {
    return onboarding_request_cache.entity_type_questions.get(cache_key);
  }

  const request = axios.get(
    GET_ENTITY_TYPE_ASSIGNED_QUESTIONS_URL,
    get_request_config({ params: { entity_type_id } })
  )
    .then((response) => normalize_collection(response).map((item) => {
      const question = item?.question || {};
      const section = item?.section || {};
      const form = item?.form || {};

      return {
        ...item,
        id: first_value([item?.section_question_id, item?.id, question?.question_id], null),
        label: first_value([question?.question_text, item?.question_text, item?.label], 'Question'),
        type: first_value([question?.question_type, item?.question_type, item?.type], 'Field'),
        form_id: first_value([form?.form_id, item?.form_id], null),
        form_name: first_value([form?.form_name, item?.form_name], ''),
        section_name: first_value([section?.section_name, item?.section_name], '')
      };
    })
      .filter((question) => question.id && question.label))
    .catch((error) => {
      onboarding_request_cache.entity_type_questions.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.entity_type_questions.set(cache_key, request);
  return request;
};

export const fetch_onboarding_execution_config = async (entity_type_id_or_options) => {
  const options = typeof entity_type_id_or_options === 'object'
    ? entity_type_id_or_options
    : { entity_type_id: entity_type_id_or_options };
  const entity_type_id = options?.entity_type_id;
  if (!entity_type_id) return null;
  const cache_key = [
    entity_type_id,
    options?.sub_entity_type_id || '',
    options?.project_id || ''
  ].map((value) => String(value)).join(':');
  if (onboarding_request_cache.full_config_by_entity_type.has(cache_key)) {
    return onboarding_request_cache.full_config_by_entity_type.get(cache_key);
  }

  const request = axios.get(
    GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
    get_request_config({
      params: {
        entity_type_id,
        ...(options?.sub_entity_type_id ? { sub_entity_type_id: options.sub_entity_type_id } : {}),
        ...(options?.project_id ? { project_id: options.project_id } : {})
      }
    })
  )
    .then((response) => parse_response_data(response))
    .catch((error) => {
      onboarding_request_cache.full_config_by_entity_type.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.full_config_by_entity_type.set(cache_key, request);
  return request;
};

export const prepare_onboarding_execution = async ({ entity_type_id, sub_entity_type_id, project_id } = {}) => {
  if (!entity_type_id) return null;

  const response = await axios.get(
    PREPARE_ONBOARDING_URL,
    get_request_config({
      params: {
        entity_type_id,
        sub_entity_type_id,
        project_id
      }
    })
  );

  return parse_response_data(response);
};

export const execute_onboarding_execution = async (payload = {}) => {
  const response = await axios.post(EXECUTE_ONBOARDING_URL, payload, get_request_config());
  onboarding_request_cache.onboarding_status.clear();
  onboarding_request_cache.application_forms.delete(String(payload?.application_id || ''));
  onboarding_request_cache.application_form.clear();
  onboarding_request_cache.all_cases.clear();
  onboarding_request_cache.assigned_cases.clear();
  onboarding_request_cache.forms_for_entity_type.clear();
  return parse_response_data(response);
};

export const fetch_onboarding_execution_status = async ({
  entity_type_id,
  entity_profile_id,
  application_id,
  sub_entity_type_id,
  project_id
} = {}) => {
  if (!entity_type_id || !entity_profile_id || !application_id) return null;
  const cache_key = [
    entity_type_id,
    entity_profile_id,
    application_id,
    sub_entity_type_id || '',
    project_id || ''
  ].map((value) => String(value)).join(':');
  if (onboarding_request_cache.onboarding_status.has(cache_key)) {
    return onboarding_request_cache.onboarding_status.get(cache_key);
  }

  const request = axios.get(
    GET_ONBOARDING_STATUS_URL,
    get_request_config({
      params: {
        entity_type_id,
        entity_profile_id,
        application_id,
        sub_entity_type_id,
        project_id
      }
    })
  )
    .then((response) => parse_response_data(response))
    .catch(async (error) => {
      if (!project_id) throw error;

      const fallback_response = await axios.get(
        GET_ONBOARDING_STATUS_URL,
        get_request_config({
          params: {
            entity_type_id,
            entity_profile_id,
            application_id,
            sub_entity_type_id
          }
        })
      );
      return parse_response_data(fallback_response);
    })
    .catch((error) => {
      onboarding_request_cache.onboarding_status.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.onboarding_status.set(cache_key, request);
  return request;
};

function get_case_runtime_status_params(case_item = {}) {
  const params = {
    entity_type_id: Number(first_value([
      case_item?.entity_type_id,
      case_item?.threshold_entity_type_id_used
    ], 0)),
    entity_profile_id: Number(case_item?.entity_profile_id || 0),
    application_id: Number(case_item?.application_id || 0),
    project_id: Number(first_value([
      case_item?.project_id,
      case_item?.onboarding_project_id
    ], 0))
  };
  return params;
}

function get_project_scoped_status_params(project_id = 0, source = {}) {
  const has_config_scope = Boolean(
    source?.project_config_binding_id
    || source?.entity_config_project_id
    || source?.onboarding_project_config_id
  );
  const scoped_params = has_config_scope && project_id > 0
    ? { project_id }
    : {};
  return scoped_params;
}

function has_runtime_status_params(params = {}) {
  return Boolean(params.entity_type_id && params.entity_profile_id && params.application_id);
}

function attach_runtime_status_to_case(case_item, onboarding_status, profile_completion = null) {
  const updated_case = {
    ...case_item,
    onboarding_status,
    ...(profile_completion ? {
      profile_completion,
      completion_percentage: profile_completion?.completion_percentage
    } : {})
  };
  return map_case(updated_case, case_item?.journey_key || infer_journey_key(updated_case));
}

async function hydrate_case_runtime_progress(case_item = {}) {
  const params = get_case_runtime_status_params(case_item);
  if (!has_runtime_status_params(params)) return case_item;

  const [onboarding_status, profile_completion] = await Promise.all([
    fetch_onboarding_execution_status({
      entity_type_id: params.entity_type_id,
      entity_profile_id: params.entity_profile_id,
      application_id: params.application_id,
      ...get_project_scoped_status_params(params.project_id, case_item)
    }).catch(() => null),
    fetch_application_profile_completion_summary(params.application_id).catch(() => null)
  ]);

  if (!onboarding_status && !profile_completion) return case_item;
  return attach_runtime_status_to_case(case_item, onboarding_status, profile_completion);
}

async function hydrate_cases_with_runtime_progress(cases = []) {
  const hydrated_cases = await map_with_concurrency_limit(
    cases,
    (case_item) => hydrate_case_runtime_progress(case_item),
    6
  );
  return hydrated_cases;
}

export const evaluate_onboarding_execution_compliance = async (payload = {}) => {
  const response = await axios.post(EVALUATE_ONBOARDING_COMPLIANCE_URL, payload, get_request_config());
  onboarding_request_cache.onboarding_status.clear();
  onboarding_request_cache.all_cases.clear();
  onboarding_request_cache.assigned_cases.clear();
  return parse_response_data(response);
};

export const fetch_onboarding_companies = async (params = {}) => {
  const response = await axios.get(VIEW_PROJECTS, get_request_config({ params }));

  return normalize_collection(response)
    .map((profile) => ({
      ...profile,
      id: first_value([profile?.id, profile?.project_id], null),
      name: first_value([profile?.name, profile?.project_name, profile?.title], 'Company'),
      project_id: first_value([profile?.id, profile?.project_id], null),
      project_type_name: first_value([profile?.project_type?.name, profile?.type?.name, profile?.project_type_name], '')
    }))
    .filter((profile) => (
      profile.id
      && profile.name
      && !ONBOARDING_EXCLUDED_PROJECT_NAMES.includes(normalize_text(profile.name))
    ));
};

export const fetch_onboarding_owners = async (params = {}) => {
  const response = await axios.get(
    GET_USERS,
    get_request_config({ params: { page_size: 500, ...params } })
  );

  return normalize_collection(response)
    .map((profile) => {
      const user = profile?.user || profile;
      const first_name = String(user?.first_name || '').trim();
      const last_name = String(user?.last_name || '').trim();
      const full_name = [first_name, last_name].filter(Boolean).join(' ');

      return {
        ...profile,
        id: first_value([profile?.user_id, user?.id, profile?.id], null),
        name: first_value([user?.name, user?.full_name, full_name, user?.username, user?.email], 'User'),
        email: first_value([user?.email, profile?.email], '')
      };
    })
    .filter((user) => user.id && user.name);
};

const add_payload_value = (payload, options, key) => {
  const value = Reflect.get(options || {}, key);
  if (Array.isArray(value) ? value.length : value) Reflect.set(payload, key, value);
};

const build_create_onboarding_case_payload = (options = {}) => {
  const payload = {
    entity_type_id: options?.entity_type_id,
    entity_name: options?.entity_name,
    form_ids: options?.form_ids
  };

  [
    'project_id',
    'owner_user_id',
    'owner_user_ids',
    'owner_role_id',
    'owner_role_ids',
    'activate_by'
  ].forEach((key) => add_payload_value(payload, options, key));

  return payload;
};

const build_local_metadata_update_response = (application_id, owner_role_id, activate_by) => ({
  success: true,
  data: {
    application_id,
    owner_role_id,
    onboarding_activate_by: activate_by || null
  }
});

export const create_onboarding_case = async (options = {}) => {
  const payload = build_create_onboarding_case_payload(options);
  const response = await axios.post(CREATE_ENTITY_APPLICATION_ONLY_URL, payload, get_request_config());
  return parse_response_data(response);
};

export const apply_for_onboarding_entity_type = async (options = {}) => {
  const payload = {
    entity_type_id: Number(options?.entity_type_id || 0),
    ...(options?.project_id ? { project_id: Number(options.project_id) } : {})
  };
  const response = await axios.post(APPLY_FOR_ENTITY_TYPE_URL, payload, get_request_config());
  return parse_response_data(response);
};

export const create_invited_onboarding_case = async (payload) => {
  const response = await axios.post(CREATE_NEW_ENTITY_URL, payload, get_request_config());
  return parse_response_data(response);
};

export const extract_onboarding_runtime_ids = (creation_response = {}) => ({
  application_id: first_value([
    creation_response?.application_id,
    creation_response?.data?.application_id,
    creation_response?.data?.application?.application_id,
    creation_response?.data?.application?.id,
    creation_response?.application?.application_id,
    creation_response?.application?.id,
    first_nested_value(creation_response, [
      'data.application.application_id',
      'data.application.id',
      'application.application_id',
      'application.id',
      'data.data.application_id',
      'data.data.application.id'
    ])
  ], null),
  entity_profile_id: first_value([
    creation_response?.entity_profile_id,
    creation_response?.data?.entity_profile_id,
    creation_response?.data?.application?.entity_profile_id,
    creation_response?.data?.entity_profile?.id,
    creation_response?.application?.entity_profile_id,
    creation_response?.entity_profile?.id,
    first_nested_value(creation_response, [
      'data.application.entity_profile_id',
      'application.entity_profile_id',
      'data.data.entity_profile_id',
      'data.data.entity_profile.id',
      'entity_profile.id'
    ])
  ], null)
});

async function find_created_onboarding_case({
  application_id,
  entity_profile_id,
  entity_type_id
} = {}) {
  const cases = await fetch_all_onboarding_cases({ _: Date.now() }).catch(() => []);
  return cases.find((case_item) => (
    application_id && Number(case_item?.application_id) === Number(application_id)
  )) || find_created_onboarding_case_by_scope(cases, { entity_profile_id, entity_type_id });
}

function find_created_onboarding_case_by_scope(cases = [], {
  entity_profile_id,
  entity_type_id
} = {}) {
  return cases.find((case_item) => (
    entity_profile_id
    && entity_type_id
    && Number(case_item?.entity_profile_id) === Number(entity_profile_id)
    && Number(case_item?.entity_type_id) === Number(entity_type_id)
  )) || cases.find((case_item) => (
    entity_type_id
    && Number(case_item?.entity_type_id) === Number(entity_type_id)
    && !case_item?.application_id
  )) || null;
}

function reconcile_runtime_ids_with_created_case(runtime_ids, created_case) {
  const application_id = first_value([runtime_ids?.application_id, created_case?.application_id], null);
  const entity_profile_id = first_value([created_case?.entity_profile_id, runtime_ids?.entity_profile_id], null);

  return Object.assign({}, {
    application_id,
    entity_profile_id
  });
}

async function resolve_created_runtime_ids(creation_response, entity_type_id) {
  const runtime_ids = extract_onboarding_runtime_ids(creation_response);
  const created_case = await find_created_onboarding_case({
    ...runtime_ids,
    entity_type_id
  });
  return reconcile_runtime_ids_with_created_case(runtime_ids, created_case);
}

function get_execute_error_message(error) {
  const message = first_value([
    error?.response?.data?.message,
    error?.response?.data?.errors,
    error?.message
  ], 'Unable to execute onboarding.');

  return typeof message === 'string' ? message : JSON.stringify(message);
}

export const execute_created_onboarding_case = async ({
  creation_response,
  entity_type_id,
  project_id,
  sub_entity_type_id,
  form_ids = []
} = {}) => {
  const runtime_ids = await resolve_created_runtime_ids(creation_response, entity_type_id);
  if (!entity_type_id || !runtime_ids.entity_profile_id || !runtime_ids.application_id) {
    throw new Error('Unable to execute onboarding because the backend did not return the application and entity profile IDs.');
  }

  const payload = {
    entity_type_id: Number(entity_type_id),
    entity_profile_id: Number(runtime_ids.entity_profile_id),
    application_id: Number(runtime_ids.application_id),
    ...(Array.isArray(form_ids) && form_ids.length ? { form_ids: form_ids.map(Number).filter(Boolean) } : {}),
    ...(sub_entity_type_id ? { sub_entity_type_id: Number(sub_entity_type_id) } : {})
  };

  const attempts = [payload];
  if (project_id) {
    attempts.push({
      ...payload,
      project_id: Number(project_id)
    });
  }

  let last_error = null;
  for (const attempt of attempts) {
    const result = await execute_onboarding_attempt(attempt);
    if (result.ok) return result.response;
    last_error = result.error;
  }

  throw new Error(get_execute_error_message(last_error));
};

async function execute_onboarding_attempt(attempt) {
  return execute_onboarding_execution(attempt)
    .then((response) => ({ ok: true, response }))
    .catch((error) => ({ ok: false, error }));
}

export const persist_local_onboarding_case_metadata = ({
  creation_response,
  onboarding_form,
  selected_project,
  selected_entity_type,
  selected_roles = [],
  selected_templates = []
}) => {
  const application_id = first_value([
    creation_response?.application_id,
    creation_response?.data?.application_id,
    first_nested_value(creation_response, ['data.application.application_id', 'application.application_id', 'application.id'])
  ], null);
  const entity_profile_id = first_value([
    creation_response?.entity_profile_id,
    creation_response?.data?.entity_profile_id,
    first_nested_value(creation_response, ['data.application.entity_profile_id', 'application.entity_profile_id'])
  ], null);

  const next_record = build_local_onboarding_case_metadata({
    application_id,
    entity_profile_id,
    onboarding_form,
    selected_project,
    selected_entity_type,
    selected_roles,
    selected_templates
  });

  const filtered_items = read_local_onboarding_case_metadata().filter((item) => !(
    (application_id && Number(item?.application_id) === Number(application_id))
    || (
      entity_profile_id
      && selected_entity_type?.id
      && Number(item?.entity_profile_id) === Number(entity_profile_id)
      && Number(item?.entity_type_id) === Number(selected_entity_type.id)
    )
  ));

  filtered_items.unshift(next_record);
  write_local_onboarding_case_metadata(filtered_items.slice(0, 50));
};

export const fetch_assigned_onboarding_cases = async (journey_key, params = {}) => {
  const requested_journey = String(journey_key || '').trim();
  const cases = await fetch_all_assigned_onboarding_cases(params);
  return requested_journey
    ? cases.filter((record) => record_matches_journey(record, requested_journey))
    : cases;
};

export const fetch_all_assigned_onboarding_cases = async (params = {}) => {
  const normalized_params = { ...(params || {}) };
  const include_runtime_progress = normalized_params.include_runtime_progress !== false;
  delete normalized_params.include_runtime_progress;
  const cache_key = JSON.stringify({
    params: normalized_params,
    include_runtime_progress
  });
  if (onboarding_request_cache.assigned_cases?.has(cache_key)) {
    return onboarding_request_cache.assigned_cases.get(cache_key);
  }

  const request = Promise.all([
    axios.get(GET_MY_ASSIGNED_APPLICATIONS_API_URL, get_request_config({ params: normalized_params })),
    fetch_onboarding_entity_types().catch(() => [])
  ])
    .then(async ([response, entity_types]) => {
      const mapped_cases = extract_items(response).map((record) => map_case(record, infer_journey_key(record)));
      const base_cases = dedupe_cases(mapped_cases);
      const hydrated_cases = include_runtime_progress
        ? await hydrate_cases_with_runtime_progress(base_cases)
        : base_cases;
      return sort_cases_newest_first(filter_cases_by_active_entity_types(hydrated_cases, entity_types));
    })
    .catch((error) => {
      onboarding_request_cache.assigned_cases?.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.assigned_cases?.set(cache_key, request);
  return request;
};

export const fetch_queue_onboarding_cases = async (params = {}) => {
  const [assigned_cases, all_cases] = await Promise.all([
    fetch_all_assigned_onboarding_cases(params).catch(() => []),
    fetch_all_onboarding_cases(params).catch(() => [])
  ]);

  return sort_cases_newest_first(dedupe_cases([...assigned_cases, ...all_cases]));
};

export async function update_onboarding_application_metadata({
  application_id,
  owner_role_id = null,
  activate_by = null
}) {
  const resolved_application_id = Number(application_id || 0);
  if (resolved_application_id <= 0) {
    throw new Error('A valid application is required.');
  }

  // There is no dedicated onboarding metadata update endpoint exposed in this
  // frontend app today, so keep the UI flow consistent and return the edited
  // values for local persistence.
  return build_local_metadata_update_response(resolved_application_id, owner_role_id, activate_by);
}

export const get_journey_stats = (cases, journey_key) => {
  const journey_label = JOURNEY_LABELS.get(journey_key) || title_case(journey_key);
  return calculate_stats(cases, journey_label);
};

const hydrate_application_form_detail = async (form, options = {}) => {
  const application_form_id = first_value([form?.application_form_id, form?.id], null);
  const [structure, application_form] = await Promise.all([
    first_value([
      form?.form?.id,
      form?.form_id
    ], null) ? fetch_form_structure(first_value([
      form?.form?.id,
      form?.form_id
    ], null)).catch(() => []) : Promise.resolve([]),
    application_form_id
      ? fetch_application_form(application_form_id, options).catch(() => null)
      : Promise.resolve(null)
  ]);
  const resolved_form_id = first_value([
    form?.form?.id,
    form?.form_id,
    application_form?.form?.id,
    application_form?.form_id,
    application_form?.form_company_id,
    application_form?.form?.form_company_id
  ], null);
  const resolved_form_name = first_value([
    form?.form?.name,
    form?.form?.form_name,
    form?.form_name,
    form?.name,
    form?.title,
    application_form?.form?.name,
    application_form?.form?.form_name,
    application_form?.form_name,
    application_form?.name,
    application_form?.title
  ], 'Application form');

  const hydrated_form = {
    ...form,
    application_form_id,
    form_id: resolved_form_id,
    form_name: resolved_form_name,
    structure,
    application_form
  };

  return hydrated_form;
};

const normalize_config_workflow_binding = (binding) => ({
  id: get_workflow_binding_id(binding),
  workflow_id: get_workflow_binding_id(binding),
  entity_workflow_id: get_workflow_binding_id(binding),
  workflow_name: first_value([
    binding?.workflow_name,
    binding?.workflow?.name,
    binding?.name,
    binding?.title
  ], ''),
  assigned_form_ids: Array.isArray(binding?.form_ids || binding?.workflow_forms)
    ? (binding.form_ids || binding.workflow_forms).map(Number).filter((value) => value > 0)
    : []
});

function get_form_assignment_id(form = {}) {
  return Number(form?.form_id || form?.id || form?.form?.id || 0);
}

function get_config_form_binding_id(binding = {}) {
  return Number(binding?.form || binding?.form_id || binding?.form?.id || binding?.id || 0);
}

function get_config_form_binding_ids(bindings = []) {
  return new Set(
    (Array.isArray(bindings) ? bindings : [])
      .filter((binding) => binding?.is_active !== false)
      .map(get_config_form_binding_id)
      .filter((form_id) => form_id > 0)
  );
}

function is_form_allowed_for_config(form_id, config_form_ids = new Set()) {
  return !config_form_ids.size || config_form_ids.has(form_id);
}

function get_assigned_workflow_form_ids(forms = [], config_form_ids = new Set()) {
  return (Array.isArray(forms) ? forms : [])
    .filter((form) => form?.isAssigned !== false && form?.is_assigned !== false)
    .map(get_form_assignment_id)
    .filter((form_id) => form_id > 0 && is_form_allowed_for_config(form_id, config_form_ids));
}

function get_status_assignment_order(status = {}, fallback_index = 0) {
  const order_candidates = [
    status?.order,
    status?.workflow_status_order,
    status?.display_order,
    status?.sequence
  ];
  const configured_order = order_candidates.map(Number).find((order) => Number.isFinite(order) && order > 0);
  return configured_order || fallback_index + 1;
}

function get_assigned_status_id(status = {}) {
  return Number(status?.status_id || status?.workflow_status_id || status?.form_status_id || 0);
}

function get_assigned_status_name(status = {}) {
  const values = [
    status?.status_name,
    status?.workflow_status_name,
    status?.status?.status,
    status?.status?.name,
    status?.name,
    status?.status
  ];
  return String(values.find((value) => String(value || '').trim()) || '').trim();
}

function get_assigned_statuses(status_data = {}) {
  const statuses = status_data?.assigned_statuses
    || status_data?.data?.assigned_statuses
    || status_data?.selected_statuses
    || status_data?.data?.selected_statuses
    || [];
  return (Array.isArray(statuses) ? statuses : [])
    .map((status, index) => ({
      status_id: get_assigned_status_id(status),
      status_name: get_assigned_status_name(status),
      order: get_status_assignment_order(status, index)
    }))
    .filter((status) => status.status_id > 0);
}

function add_assigned_status_order(status_order_map, status = {}) {
  const existing_status = status_order_map.get(status.status_id);
  const next_order = Number(status.order || 0);
  if (!existing_status || (next_order > 0 && next_order < existing_status.order)) {
    status_order_map.set(status.status_id, status);
  }
  return status_order_map;
}

function build_form_scoped_statuses(results = []) {
  return results
    .filter((result) => result.status === 'fulfilled')
    .flatMap((result) => get_assigned_statuses(result.value))
    .reduce(add_assigned_status_order, new Map());
}

function map_form_status_group(status_order_map = new Map(), workflow_id = 0, workflow_order = 0) {
  return Array.from(status_order_map.values())
    .sort((left, right) => left.order - right.order)
    .map(({ status_id, status_name, order }) => ({
      status_id,
      status_name,
      order,
      workflow_status_order: order,
      entity_workflow_id: workflow_id,
      workflow_id,
      workflow_order: workflow_order || 1
    }));
}

function get_workflow_status_id(status = {}) {
  return Number(
    status?.status_id
    || status?.workflow_status_id
    || status?.form_status_id
    || status?.status?.id
    || 0
  );
}

function get_configured_workflow_order(status = {}) {
  const order_candidates = [
    status?.order,
    status?.workflow_status_order,
    status?.display_order,
    status?.sequence
  ];
  return order_candidates.map(Number).find((order) => Number.isFinite(order) && order > 0) || null;
}

function apply_workflow_status_order(status = {}, assigned_status = {}) {
  const order = get_configured_workflow_order(status) || assigned_status.order;
  return {
    ...assigned_status,
    status_name: get_assigned_status_name(status) || assigned_status.status_name,
    order,
    workflow_status_order: order
  };
}

function map_workflow_ordered_form_status_group(status_order_map = new Map(), workflow_statuses = [], workflow_id = 0, workflow_order = 0) {
  const ordered_statuses = (Array.isArray(workflow_statuses) ? workflow_statuses : [])
    .map((status) => {
      const status_id = get_workflow_status_id(status);
      const assigned_status = status_order_map.get(status_id);
      return assigned_status ? {
        ...apply_workflow_status_order(status, assigned_status),
        entity_workflow_id: Number(
          first_value([
            status?.entity_workflow_id,
            status?.workflow_id,
            status?.workflow?.id
          ], workflow_id || 0)
        ),
        workflow_id: Number(
          first_value([
            status?.workflow_id,
            status?.entity_workflow_id,
            status?.workflow?.id
          ], workflow_id || 0)
        ),
        workflow_order: Number(
          first_value([
            status?.workflow_order,
            status?.entity_workflow_order
          ], workflow_order || 1)
        )
      } : null;
    })
    .filter(Boolean)
    .sort((left, right) => left.order - right.order);

  return ordered_statuses.length ? ordered_statuses : map_form_status_group(status_order_map, workflow_id, workflow_order);
}

async function build_form_scoped_workflow_status_group(workflow_id, config_form_ids = new Set(), workflow_order = 0) {
  const workflow_forms = await fetch_entity_type_workflow_forms({
    entity_workflow_id: workflow_id,
    workflow_id
  }).catch(() => []);
  const form_ids = get_assigned_workflow_form_ids(workflow_forms, config_form_ids);
  if (!form_ids.length) return [];

  const form_status_results = await Promise.allSettled(
    form_ids.map((form_id) => fetch_assign_form_status_data({ form_id }))
  );
  const assigned_status_order_map = build_form_scoped_statuses(form_status_results);
  if (!assigned_status_order_map.size) return [];

  const workflow_statuses = await fetch_workflow_statuses({ entity_workflow_id: workflow_id }).catch(() => []);
  return map_workflow_ordered_form_status_group(
    assigned_status_order_map,
    workflow_statuses,
    workflow_id,
    workflow_order
  );
}

const dedupe_workflows = (workflows) => {
  const workflow_map = new Map();
  workflows.forEach((workflow) => {
    const workflow_id = Number(workflow?.id || workflow?.workflow_id || workflow?.entity_workflow_id || 0);
    if (workflow_id > 0 && !workflow_map.has(workflow_id)) workflow_map.set(workflow_id, workflow);
  });
  return Array.from(workflow_map.values());
};

const hydrate_workflow_statuses = async (workflow, workflow_index) => {
  const workflow_id = Number(workflow?.id || workflow?.workflow_id || workflow?.entity_workflow_id || 0);
  if (workflow_id <= 0) return [];

  const statuses = await fetch_workflow_statuses({ entity_workflow_id: workflow_id }).catch(() => []);
  return (Array.isArray(statuses) ? statuses : []).map((status, status_index) => ({
    ...status,
    entity_workflow_id: workflow_id,
    workflow_name: first_value([workflow?.workflow_name, workflow?.name, workflow?.title], ''),
    workflow_order: Number(workflow_index + 1),
    workflow_status_order: Number(status_index + 1)
  }));
};

async function fetch_case_workflow_statuses({ entity_type_id, project_id } = {}) {
  if (entity_type_id <= 0) return [];

  const config_response = await fetch_onboarding_execution_config({
    entity_type_id,
    ...(project_id > 0 ? { project_id } : {})
  }).catch(() => null);
  const config_workflows = extract_config_workflow_bindings(config_response)
    .map(normalize_config_workflow_binding)
    .filter((workflow) => Number(workflow?.id) > 0);
  const config_form_ids = get_config_form_binding_ids(extract_config_form_bindings(config_response));
  const resolved_workflows = dedupe_workflows(config_workflows);
  const [workflow_status_results, form_scoped_status_results] = await Promise.all([
    Promise.all(resolved_workflows.map((workflow, workflow_index) => hydrate_workflow_statuses(workflow, workflow_index))),
    Promise.all(
      resolved_workflows.map((workflow, workflow_index) => build_form_scoped_workflow_status_group(
        Number(workflow?.id || workflow?.workflow_id || workflow?.entity_workflow_id || 0),
        config_form_ids,
        workflow_index + 1
      ))
    )
  ]);

  return [...form_scoped_status_results.flat(), ...workflow_status_results.flat()];
}

function extract_gate_ids_from_workflow_statuses(workflow_statuses = []) {
  const gate_ids = new Set();
  (Array.isArray(workflow_statuses) ? workflow_statuses : []).forEach((status) => {
    const gate_id = normalize_gate_stage_id(get_assigned_status_name(status));
    if (gate_id) gate_ids.add(gate_id);
  });
  return Array.from(gate_ids);
}

export async function fetch_configured_case_gate_ids(case_item = {}) {
  const preloaded_gate_ids = extract_gate_ids_from_workflow_statuses(case_item?.workflow_statuses);
  if (preloaded_gate_ids.length) return preloaded_gate_ids;

  const entity_type_id = Number(first_value([
    case_item?.entity_type_id,
    case_item?.threshold_entity_type_id_used,
    case_item?.entity_type?.id
  ], 0));
  const project_id = Number(first_value([
    case_item?.project_id,
    case_item?.onboarding_project_id,
    case_item?.profile?.project_id,
    case_item?.profile?.project?.id
  ], 0));

  if (!entity_type_id) return [];

  const workflow_statuses = await fetch_case_workflow_statuses({
    entity_type_id,
    project_id
  }).catch(() => []);
  return extract_gate_ids_from_workflow_statuses(workflow_statuses);
}

const apply_workflow_stage_to_case = (hydrated_case) => {
  const onboarding_status = hydrated_case?.onboarding_status || {};
  const completion = onboarding_status?.completion || {};
  const status_stage = first_value([
    onboarding_status?.workflow?.current_status,
    onboarding_status?.workflow?.current_stage,
    onboarding_status?.workflow?.status,
    hydrated_case?.current_workflow_status
  ], '');
  const workflow_stage = resolve_workflow_display_stage(hydrated_case);
  const resolved_stage = first_value([workflow_stage, status_stage], '');
  if (!resolved_stage && !Object.keys(completion).length && onboarding_status?.parent_complete !== true) {
    return hydrated_case;
  }

  const next_case = {
    ...hydrated_case,
    current_workflow_status: resolved_stage || hydrated_case?.current_workflow_status,
    stage: resolved_stage || hydrated_case?.stage,
    detail: resolved_stage || hydrated_case?.detail,
    activation: onboarding_status?.parent_complete ? 'Complete' : (resolved_stage || hydrated_case?.activation),
    workflow_complete: completion?.workflow_complete === true || onboarding_status?.parent_complete === true,
    parent_complete: onboarding_status?.parent_complete === true,
    onboarding_completion: completion
  };
  return map_case(next_case, next_case?.journey_key || infer_journey_key(next_case));
};

function has_runtime_workflow_statuses(onboarding_status) {
  const statuses = onboarding_status?.workflow?.statuses;
  return Array.isArray(statuses) && statuses.length > 0;
}

function is_child_onboarding_case(case_item = {}, profile = null) {
  const current_entity_id = Number(first_value([
    case_item?.current_entity?.id,
    case_item?.current_entity,
    profile?.current_entity?.id
  ], 0));
  const parent_entity_id = Number(first_value([
    case_item?.entity?.id,
    case_item?.entity,
    profile?.entity?.id
  ], 0));

  return current_entity_id > 0 && parent_entity_id > 0 && current_entity_id !== parent_entity_id;
}

export const hydrate_case_detail = async (case_item, options = {}) => {
  const include_runtime_status = options?.include_runtime_status !== false;
  const include_onboarding_config = options?.include_onboarding_config !== false;
  const include_workflow_statuses = options?.include_workflow_statuses !== false;
  const include_documents = options?.include_documents !== false;
  const include_detailed_forms = options?.include_detailed_forms !== false;
  const force_refresh_forms = options?.force_refresh_forms === true;
  const entity_profile_id = Number(case_item?.entity_profile_id || 0);
  const application_id = Number(case_item?.application_id || 0);
  const project_id = Number(first_value([
    case_item?.project_id,
    case_item?.onboarding_project_id,
    case_item?.profile?.project_id,
    case_item?.profile?.project?.id
  ], 0));
  const [profile, forms, documents, agreements] = await Promise.all([
    fetch_entity_profile(case_item?.entity_profile_id),
    fetch_application_forms(case_item?.application_id, {
      force_refresh: force_refresh_forms
    }),
    include_documents
      ? fetch_application_documents(case_item?.application_id)
      : Promise.resolve([]),
    fetch_sent_contracts(case_item?.entity_profile_id).catch(() => [])
  ]);
  const resolved_entity_type_id = Number(first_value([
    case_item?.threshold_entity_type_id_used,
    case_item?.entity_type_entity_profile?.entity_type_id,
    case_item?.entity_type_entity_profile?.entity_type?.id,
    profile?.entity_type?.id,
    profile?.entity_type_id,
    case_item?.entity_type_id
  ], 0));
  const should_fetch_runtime_status = include_runtime_status && !is_child_onboarding_case(case_item, profile);
  const detailed_forms_promise = include_detailed_forms
    ? Promise.all((Array.isArray(forms) ? forms : []).map((form) => hydrate_application_form_detail(form, {
      force_refresh: force_refresh_forms
    })))
    : Promise.resolve([]);
  const workflow_statuses_promise = include_workflow_statuses
    ? fetch_case_workflow_statuses({
      entity_type_id: resolved_entity_type_id,
      project_id
    }).catch(() => [])
    : Promise.resolve([]);
  const [onboarding_config, onboarding_status, detailed_forms, workflow_statuses] = await Promise.all([
    include_onboarding_config
      ? fetch_onboarding_execution_config({
          entity_type_id: resolved_entity_type_id,
          ...(project_id > 0 ? { project_id } : {})
        }).catch(() => null)
      : Promise.resolve(null),
    should_fetch_runtime_status
      ? fetch_onboarding_execution_status({
          entity_type_id: resolved_entity_type_id,
          entity_profile_id,
          application_id,
          ...get_project_scoped_status_params(project_id, case_item)
        }).catch(() => null)
      : Promise.resolve(null),
    detailed_forms_promise,
    workflow_statuses_promise
  ]);

  return apply_workflow_stage_to_case({
    ...case_item,
    profile,
    forms,
    detailed_forms,
    documents,
    agreements,
    workflow_statuses,
    onboarding_config,
    onboarding_status
  });
};



